// text-align.component.ts

import { DOCUMENT, NgIf, NgClass, NgFor } from "@angular/common";
import { Component, OnInit, OnDestroy, Renderer2, inject } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccessibilityService } from "./services/accessibility.service";
import {AstralCheckmarkSvgComponent} from "./util/astral-checksvg.component";

@Component({
  selector: "tgsd-text-align",
  standalone: true,
  // TEMPLATE: Replaced with the standard template for consistency.
  template: `
    <button
      (click)="nextState()"
      [ngClass]="{ 'in-use': currentState !== 0 }"
      aria-label="Cycle through text size options"
    >
      <div class="title">
        <div class="icon-state-wrap">
          <div
            class="icon action-icon "
            [ngClass]="{
              inactive: currentState == 0,
              active: currentState != 0
            }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 19h18v2H3zM3 7h12v2H3zm0-4h18v2H3zm0 12h12v2H3z"/>
            </svg>
          </div>

          <div class="state-dots-wrap">
            <span>{{ states[currentState] }}</span>
            <div
              class="dots"
              [ngClass]="{ inactive: currentState === 0 }"
            >
              <div
                class="dot"
                [ngClass]="{ active: currentState === 1 }"
              ></div>
              <div
                class="dot"
                [ngClass]="{ active: currentState === 2 }"
              ></div>
              <div
                class="dot"
                [ngClass]="{
                  active: currentState === 3
                }"
              ></div>
              <div
                class="dot"
                [ngClass]="{
                  active: currentState === 4
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <astral-widget-checkmark
        [isActive]="currentState !== 0"
      ></astral-widget-checkmark>
    </button>
  `,
  imports: [NgIf, NgClass, NgFor, AstralCheckmarkSvgComponent],
  // STYLES: Using the same minimalist style as the contrast component.
  styles: [`
    :host {
      display: block;
    }
    .feature-item {
      position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center;
      width: 100%; min-height: 90px; padding: 12px 8px; text-align: center;
      background-color: transparent; border: none; color: #333; cursor: pointer;
      transition: color 0.2s ease-in-out; font-family: inherit;
    }
    .feature-item.active {
      color: #007bff;
    }
    .active-tick {
      position: absolute; top: 5px; right: 5px; color: #28a745;
    }
    .active-tick svg {
      width: 20px; height: 20px;
    }
    .feature-icon {
      margin-bottom: 8px;
    }
    .feature-icon svg {
      width: 28px; height: 28px;
    }
    .feature-label {
      font-size: 13px; font-weight: 600; line-height: 1.3;
    }
    .state-indicators {
      position: absolute; bottom: 8px; display: flex; gap: 5px; justify-content: center;
    }
    .state-dot {
      width: 7px; height: 7px; border-radius: 50%; background-color: rgba(0, 0, 0, 0.2);
      transition: all 0.2s ease;
    }
    .state-dot.is-active {
      background-color: #007bff; transform: scale(1.1);
    }
  `]
})
export class TextAlignComponent implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private accessibilityService = inject(AccessibilityService);
  private destroy$ = new Subject<void>();

  public currentState = 0;
  public readonly states = ["Text Align", "Left", "Center", "Right", "Justify"];
  private _styleElement: HTMLStyleElement | null = null;

  ngOnInit(): void {
    this.accessibilityService.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetToDefault());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._cleanupFeature();
  }

  public nextState(): void {
    this.currentState = (this.currentState + 1) % this.states.length;
    this._runStateLogic();
  }

  private resetToDefault(): void {
    if (this.currentState !== 0) {
      this.currentState = 0;
      this._runStateLogic();
    }
  }

  public getAriaLabel(): string {
    const nextStateName = this.states[(this.currentState + 1) % this.states.length];
    return `Text Align feature. Current state: ${this.states[this.currentState]}. Click to change to ${nextStateName}.`;
  }

  private _cleanupFeature(): void {
    // Safely remove the style element if it exists.
    this._styleElement?.remove();
    this._styleElement = null;
  }

  private _runStateLogic(): void {
    this._cleanupFeature();

    if (this.currentState === 0) { // Base state
      return;
    }

    const alignment = this.states[this.currentState].toLowerCase();

    // ARCHITECTURE: Inject a single stylesheet to control text alignment globally.
    // This targets common block-level text containers.
    const css = `
      p, li, th, td, h1, h2, h3, h4, h5, h6, div:not(#ega-accessibility-widget *) {
        text-align: ${alignment} !important;
      }
    `;

    // Create the style element
    this._styleElement = this.renderer.createElement('style');

    // ✅ Null Safety Check: Proceed only if the element was created successfully.
    if (this._styleElement) {
      // ✅ Safe DOM Manipulation: All operations are inside the null guard.
      this._styleElement.textContent = css;
      this.renderer.appendChild(this.document.head, this._styleElement);
    }
  }
}
